import { useState } from 'react';
import { EngineeringNav } from '@/components/EngineeringNav';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Send, CheckCircle, Paperclip, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EngineeringQuote() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    project_description: ''
  });
  const [attachment, setAttachment] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error('File size must be less than 10MB');
        return;
      }
      setAttachment(file);
    }
  };

  const removeAttachment = () => {
    setAttachment(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.service_type || !formData.project_description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      // Use FormData to send file directly with the form
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone || '');
      submitData.append('service_type', formData.service_type);
      submitData.append('project_description', formData.project_description);
      
      if (attachment) {
        submitData.append('file', attachment);
      }

      await axios.post(`${API}/quotes-with-file`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setIsSubmitted(true);
      toast.success('Quote request submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit quote request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-black" data-testid="quote-success">
        <EngineeringNav />
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <div className="w-20 h-20 bg-[#F54021]/20 border-2 border-[#F54021] rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-[#F54021]" />
          </div>
          <h1 className="font-nunito text-3xl font-bold text-white mb-4">
            Quote Request Received!
          </h1>
          <p className="font-nunito text-gray-400 mb-8">
            Thank you for your interest. We'll review your project details and get back to you within 24-48 hours.
          </p>
          <Button 
            onClick={() => setIsSubmitted(false)}
            className="bg-[#F54021] hover:bg-[#ff5a35] font-nunito font-bold rounded-full"
            data-testid="submit-another-btn"
          >
            Submit Another Quote
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="engineering-quote">
      <EngineeringNav />
      
      {/* Hero */}
      <section className="bg-[#0a0a0a] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="font-nunito text-sm text-[#F54021] mb-4 tracking-wider uppercase font-bold">Get a Quote</p>
            <h1 className="font-nunito text-4xl lg:text-5xl font-extrabold text-white mb-6">
              Let's Discuss Your Project
            </h1>
            <p className="font-nunito text-lg text-gray-400">
              Fill out the form below with your project details and we'll provide a detailed quote within 24-48 hours.
            </p>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="font-nunito font-semibold text-white">
                  Name <span className="text-[#F54021]">*</span>
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your name"
                  className="bg-[#1a1a1a] border-[#333333] text-white focus:border-[#F54021] focus:ring-[#F54021] font-nunito"
                  data-testid="quote-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email" className="font-nunito font-semibold text-white">
                  Email <span className="text-[#F54021]">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="your@email.com"
                  className="bg-[#1a1a1a] border-[#333333] text-white focus:border-[#F54021] focus:ring-[#F54021] font-nunito"
                  data-testid="quote-email-input"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="phone" className="font-nunito font-semibold text-white">
                  Phone (Optional)
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Your phone number"
                  className="bg-[#1a1a1a] border-[#333333] text-white focus:border-[#F54021] focus:ring-[#F54021] font-nunito"
                  data-testid="quote-phone-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="service" className="font-nunito font-semibold text-white">
                  Service Type <span className="text-[#F54021]">*</span>
                </Label>
                <Select 
                  value={formData.service_type} 
                  onValueChange={(value) => setFormData({ ...formData, service_type: value })}
                >
                  <SelectTrigger className="bg-[#1a1a1a] border-[#333333] text-white font-nunito" data-testid="quote-service-select">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-[#333333]">
                    <SelectItem value="cnc_plasma" className="text-white font-nunito">CNC Plasma Cutting</SelectItem>
                    <SelectItem value="cad_design" className="text-white font-nunito">CAD Design</SelectItem>
                    <SelectItem value="3d_scanning" className="text-white font-nunito">3D Scanning</SelectItem>
                    <SelectItem value="technical_drawing" className="text-white font-nunito">Technical Drawing</SelectItem>
                    <SelectItem value="multiple" className="text-white font-nunito">Multiple Services</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="font-nunito font-semibold text-white">
                Project Description <span className="text-[#F54021]">*</span>
              </Label>
              <Textarea
                id="description"
                value={formData.project_description}
                onChange={(e) => setFormData({ ...formData, project_description: e.target.value })}
                placeholder="Please describe your project requirements, including materials, dimensions, quantities, and any specific requirements..."
                rows={6}
                className="bg-[#1a1a1a] border-[#333333] text-white focus:border-[#F54021] focus:ring-[#F54021] resize-none font-nunito"
                data-testid="quote-description-input"
              />
            </div>

            {/* File Attachment */}
            <div className="space-y-2">
              <Label className="font-nunito font-semibold text-white">
                Attachment (Optional)
              </Label>
              {attachment ? (
                <div className="flex items-center gap-3 p-3 bg-[#1a1a1a] border border-[#333333] rounded-lg">
                  <Paperclip className="w-5 h-5 text-[#F54021]" />
                  <span className="font-nunito text-sm text-gray-300 flex-1 truncate">{attachment.name}</span>
                  <button 
                    type="button"
                    onClick={removeAttachment}
                    className="p-1 hover:bg-[#333333] rounded"
                  >
                    <X className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              ) : (
                <label className="flex items-center gap-3 p-3 bg-[#1a1a1a] border border-[#333333] border-dashed rounded-lg cursor-pointer hover:border-[#F54021] transition-colors">
                  <Paperclip className="w-5 h-5 text-gray-500" />
                  <span className="font-nunito text-sm text-gray-500">Click to attach a file (max 10MB)</span>
                  <input 
                    type="file" 
                    className="hidden" 
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.dxf,.dwg,.step,.stp,.stl"
                    data-testid="quote-file-input"
                  />
                </label>
              )}
              <p className="font-nunito text-xs text-gray-500">
                Supported: PDF, DOC, Images, DXF, DWG, STEP, STL
              </p>
            </div>

            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="w-full md:w-auto px-8 py-3 bg-[#F54021] hover:bg-[#ff5a35] font-nunito font-bold rounded-full"
              data-testid="submit-quote-btn"
            >
              {isSubmitting ? (
                'Submitting...'
              ) : (
                <>
                  Submit Quote Request
                  <Send className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </form>

          {/* Info Box */}
          <div className="mt-12 bg-[#1a1a1a] rounded-2xl p-6 border border-[#333333]">
            <h3 className="font-nunito font-bold text-white mb-4">What happens next?</h3>
            <ol className="space-y-3 font-nunito text-sm text-gray-400">
              <li className="flex gap-3">
                <span className="text-[#F54021] font-bold">01</span>
                We review your project requirements
              </li>
              <li className="flex gap-3">
                <span className="text-[#F54021] font-bold">02</span>
                We may contact you for additional details
              </li>
              <li className="flex gap-3">
                <span className="text-[#F54021] font-bold">03</span>
                You receive a detailed quote within 24-48 hours
              </li>
              <li className="flex gap-3">
                <span className="text-[#F54021] font-bold">04</span>
                Upon approval, we begin your project
              </li>
            </ol>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-[#333333] py-12 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-nunito text-sm text-gray-500 text-center">
            © 2024 BJ Engineering. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
